import {
    getVariables,
    buildVarRef,
    getTargetStyleValue,
    setTargetStyleValue,
} from './variables';
import { TYPE_COLOR, TYPE_SIZE } from './types';

const COLOR_PROPERTIES = [
    ['typography', 'color'],
    ['decorations', 'background-color'],
];

const COLOR_SUB_PROPERTIES = [
    'border-color',
    'outline-color',
    'text-decoration-color',
    'column-rule-color',
];

const SIZE_PROPERTIES = [
    ['dimension', 'width'],
    ['dimension', 'height'],
    ['dimension', 'min-width'],
    ['dimension', 'max-width'],
    ['dimension', 'min-height'],
    ['dimension', 'max-height'],
    ['general', 'top'],
    ['general', 'right'],
    ['general', 'bottom'],
    ['general', 'left'],
    ['typography', 'font-size'],
    ['typography', 'letter-spacing'],
    ['typography', 'line-height'],
];

const SIZE_SUB_PROPERTIES = [
    'margin-top',
    'margin-right',
    'margin-bottom',
    'margin-left',
    'padding-top',
    'padding-right',
    'padding-bottom',
    'padding-left',
    'border-width',
    'border-top-left-radius',
    'border-top-right-radius',
    'border-bottom-right-radius',
    'border-bottom-left-radius',
];

const SIZE_EXTRA_PROPERTIES = [
    ['extra', 'column-gap'],
    ['extra', 'row-gap'],
];

const PENCIL_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>';

let smStylesInjected = false;

const injectSmStyles = () => {
    if (smStylesInjected) return;
    smStylesInjected = true;

    const smStyles = document.createElement('style');
    smStyles.textContent = `
  .css-vars-sm-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    flex: 1;
  }
  .css-vars-sm-wrapper .gjs-field {
    flex: 1;
  }

  .css-vars-sm-trigger {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    margin-left: 4px;
    opacity: 0.4;
    transition: opacity 0.15s;
    vertical-align: middle;
    cursor: pointer;
  }
  .css-vars-sm-trigger:hover {
    opacity: 1;
  }
  .css-vars-sm-trigger__icon {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 0;
    background: transparent;
    color: inherit;
    font-size: 11px;
    font-weight: normal;
    line-height: 1;
    pointer-events: none;
  }
  .css-vars-sm-trigger__select {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    border: none;
    background: transparent;
    font-size: 0;
    z-index: 1;
  }
  .css-vars-sm-trigger__select option {
    color: #ddd;
    background: #3b3b3b;
    font-size: 12px;
  }

  .css-vars-sm-pill {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #fff;
    border-radius: 3px;
    padding: var(--gjs-input-padding);
    font-size: 13px;
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
    flex: 1;
    border-radius: 2px;
    background: var(--gjs-main-dark-color);
    line-height: 1.6;
  }
  .css-vars-sm-pill__swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    border: 1px solid rgba(255,255,255,0.4);
    flex-shrink: 0;
  }
  .css-vars-sm-pill__name {
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  }

  .css-vars-sm-clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 11px;
    height: 16px;
    flex-shrink: 0;
    margin-left: 4px;
    border-radius: 0;
    background: transparent;
    color: inherit;
    border: none;
    cursor: pointer;
    font-size: 16px;
    font-weight: normal;
    opacity: 1;
    transition: opacity 0.15s;
  }
  .css-vars-sm-wrapper--has-var .gjs-field {
    display: none;
  }
  .css-vars-sm-wrapper--has-var .css-vars-sm-trigger {
    display: none;
  }
`;
    document.head.appendChild(smStyles);
};

function injectVarUI(editor, property, variables) {
    const view = property.view;
    if (!view || !view.el) return;

    const oldWrapper = view.el.querySelector('.css-vars-sm-wrapper');
    if (oldWrapper) {
        const field = oldWrapper.querySelector('.gjs-field');
        if (field) oldWrapper.parentNode.insertBefore(field, oldWrapper);
        oldWrapper.remove();
    }
    view.el
        .querySelectorAll('.css-vars-sm-trigger, .css-vars-sm-pill, .css-vars-sm-clear')
        .forEach((el) => el.remove());

    if (variables.length === 0) return;

    const fieldEl = view.el.querySelector('.gjs-field');
    if (!fieldEl) return;

    const targetValue = getTargetStyleValue(editor, property);
    const ownVarMatch = targetValue.match(/var\([^)]+\)/);
    const ownVarRef = ownVarMatch ? ownVarMatch[0] : '';
    const isOwnVar = !!ownVarRef;
    const ownMatchedVar = isOwnVar
        ? variables.find((v) => v.ref === ownVarRef)
        : null;

    const wrapper = document.createElement('div');
    wrapper.className = 'css-vars-sm-wrapper';
    if (isOwnVar && ownMatchedVar) {
        wrapper.classList.add('css-vars-sm-wrapper--has-var');
    }

    fieldEl.parentNode.insertBefore(wrapper, fieldEl);
    wrapper.appendChild(fieldEl);

    const labelEl =
        view.el.querySelector('.gjs-sm-label .gjs-sm-icon') ||
        view.el.querySelector('.gjs-sm-label');

    if (isOwnVar && ownMatchedVar) {
        const pill = document.createElement('div');
        pill.className = 'css-vars-sm-pill';

        if (ownMatchedVar.type === TYPE_COLOR && ownMatchedVar.value) {
            const swatch = document.createElement('span');
            swatch.className = 'css-vars-sm-pill__swatch';
            swatch.style.background = ownMatchedVar.value;
            pill.appendChild(swatch);
        }

        const nameSpan = document.createElement('span');
        nameSpan.className = 'css-vars-sm-pill__name';
        nameSpan.textContent = ownMatchedVar.name;
        nameSpan.title = ownVarRef;
        pill.appendChild(nameSpan);

        wrapper.appendChild(pill);

        const clearBtn = document.createElement('button');
        clearBtn.className = 'css-vars-sm-clear';
        clearBtn.textContent = '\u00d7';
        clearBtn.title = 'Remove variable';
        clearBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            setTargetStyleValue(editor, property, '');
        });
        if (labelEl) {
            labelEl.appendChild(clearBtn);
        } else {
            wrapper.appendChild(clearBtn);
        }
    } else {
        const trigger = document.createElement('div');
        trigger.className = 'css-vars-sm-trigger';

        const icon = document.createElement('span');
        icon.className = 'css-vars-sm-trigger__icon';
        icon.innerHTML = PENCIL_ICON_SVG;
        trigger.appendChild(icon);

        const select = document.createElement('select');
        select.className = 'css-vars-sm-trigger__select';
        select.title = 'Use variable';

        const emptyOpt = document.createElement('option');
        emptyOpt.value = '';
        emptyOpt.textContent = '\u2014';
        emptyOpt.selected = true;
        select.appendChild(emptyOpt);

        for (const v of variables) {
            const opt = document.createElement('option');
            opt.value = v.ref;
            opt.textContent = v.name;
            select.appendChild(opt);
        }

        select.addEventListener('change', (e) => {
            e.stopPropagation();
            const ref = e.target.value;
            if (ref) {
                setTargetStyleValue(editor, property, ref);
            }
        });

        trigger.appendChild(select);

        if (labelEl) {
            labelEl.appendChild(trigger);
        } else {
            wrapper.appendChild(trigger);
        }
    }
}

function injectOnCompositeSubProperties(
    editor,
    sectorId,
    propertyId,
    subPropertyNames,
    variables
) {
    const prop = editor.StyleManager.getProperty(sectorId, propertyId);
    if (!prop) return;
    const subProps = prop.getProperties ? prop.getProperties() : [];
    for (const sub of subProps) {
        const propName = sub.get('property');
        if (subPropertyNames.includes(propName)) {
            injectVarUI(editor, sub, variables);
        }
    }

    if (!prop.__cssVarClearHooked && prop.view && prop.view.el) {
        const clearBtn = prop.view.el.querySelector('[data-clear-style]');
        if (clearBtn) {
            prop.__cssVarClearHooked = true;
            clearBtn.addEventListener('click', () => {
                for (const sub of prop.getProperties ? prop.getProperties() : []) {
                    const pName = sub.get('property');
                    if (subPropertyNames.includes(pName)) {
                        const val = getTargetStyleValue(editor, sub);
                        if (val && val.includes('var(')) {
                            setTargetStyleValue(editor, sub, '');
                        }
                    }
                }
            });
        }
    }
}

function buildVarOptions(editor) {
    const vars = getVariables(editor);

    const colorOptions = vars.colors.map((v) => ({
        name: v.name,
        ref: buildVarRef(v.name),
        type: TYPE_COLOR,
        value: v.value,
    }));

    const sizeOptions = vars.sizes.map((v) => ({
        name: v.name,
        ref: buildVarRef(v.name),
        type: TYPE_SIZE,
        value: v.value,
    }));

    return { colorOptions, sizeOptions };
}

function injectAllDropdowns(editor, options) {
    const { colorOptions, sizeOptions } = buildVarOptions(editor);

    if (options.enableColors !== false) {
        for (const [sector, prop] of COLOR_PROPERTIES) {
            const property = editor.StyleManager.getProperty(sector, prop);
            if (property) injectVarUI(editor, property, colorOptions);
        }
        injectOnCompositeSubProperties(
            editor,
            'decorations',
            'outline',
            COLOR_SUB_PROPERTIES,
            colorOptions
        );
        injectOnCompositeSubProperties(
            editor,
            'decorations',
            'border',
            COLOR_SUB_PROPERTIES,
            colorOptions
        );
        injectOnCompositeSubProperties(
            editor,
            'typography',
            'text-decoration',
            COLOR_SUB_PROPERTIES,
            colorOptions
        );
        injectOnCompositeSubProperties(
            editor,
            'extra',
            'column-rule',
            COLOR_SUB_PROPERTIES,
            colorOptions
        );
    }

    if (options.enableSizes !== false) {
        for (const [sector, prop] of SIZE_PROPERTIES) {
            const property = editor.StyleManager.getProperty(sector, prop);
            if (property) injectVarUI(editor, property, sizeOptions);
        }
        for (const [sector, prop] of SIZE_EXTRA_PROPERTIES) {
            const property = editor.StyleManager.getProperty(sector, prop);
            if (property) injectVarUI(editor, property, sizeOptions);
        }
        injectOnCompositeSubProperties(
            editor,
            'dimension',
            'margin',
            SIZE_SUB_PROPERTIES,
            sizeOptions
        );
        injectOnCompositeSubProperties(
            editor,
            'dimension',
            'padding',
            SIZE_SUB_PROPERTIES,
            sizeOptions
        );
        injectOnCompositeSubProperties(
            editor,
            'decorations',
            'border-radius',
            SIZE_SUB_PROPERTIES,
            sizeOptions
        );
        injectOnCompositeSubProperties(
            editor,
            'decorations',
            'border',
            SIZE_SUB_PROPERTIES,
            sizeOptions
        );
    }
}

export function refreshStyleManager(editor, options) {
    requestAnimationFrame(() => {
        injectAllDropdowns(editor, options);
    });
}

export function setupStyleManager(editor, options) {
    injectSmStyles();

    let debounceTimeout;
    const inject = () => {
        clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(() => {
            requestAnimationFrame(() => {
                injectAllDropdowns(editor, options);
            });
        }, 60);
    };

    editor.on('component:selected', inject);
    editor.on('style:target', inject);
    editor.on('style:sector:update', inject);
    editor.on('style:property:update', inject);
    editor.on('undo', inject);
    editor.on('redo', inject);

    editor.on('load', () => {
        setTimeout(inject, 200);
    });
}
